// Scanner-friendly serial entry. One auto-focused input commits on Enter
// (which is what every USB barcode/IMEI scanner sends after a successful read)
// and pushes the serial into a chip list. Falls back to a paste textarea for
// users without a scanner.
//
// Dedupe is client-side warn-only; the backend still enforces uniqueness via
// the serials.serial_no UNIQUE constraint, so a dupe slipping through is safe.

import { useEffect, useRef, useState } from "react";
import { ScanLine, ClipboardPaste, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils/cn";
import { useScannerAutoSubmit } from "./use-scanner-auto-submit";

interface Props {
  value: string[];
  onChange: (serials: string[]) => void;
  /** Optional cap to warn (e.g. quantity). 0 = no cap. */
  expectedCount?: number;
}

type Mode = "scan" | "paste";

export function SerialScanInput({ value, onChange, expectedCount = 0 }: Props) {
  const [mode, setMode] = useState<Mode>("scan");
  const [scanValue, setScanValue] = useState("");
  const [pasteValue, setPasteValue] = useState("");
  const [warn, setWarn] = useState<string | null>(null);
  const scanRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (mode === "scan") {
      const t = setTimeout(() => scanRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [mode]);

  function commitOne(raw: string) {
    const s = raw.trim();
    if (!s) return;
    if (value.includes(s)) {
      setWarn(`Đã quét rồi: ${s}`);
      return;
    }
    setWarn(null);
    onChange([...value, s]);
  }

  // Máy quét không gửi Enter → tự thêm vào danh sách khi vừa tít xong.
  useScannerAutoSubmit(
    scanValue,
    () => {
      commitOne(scanValue);
      setScanValue("");
    },
    { enabled: mode === "scan" },
  );

  function handleScanKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== "Enter") return;
    e.preventDefault();
    commitOne(scanValue);
    setScanValue("");
  }

  function commitPaste() {
    const toks = pasteValue.split(/[\s,;]+/).map((t) => t.trim()).filter(Boolean);
    const seen = new Set(value);
    const added: string[] = [];
    const dupes: string[] = [];
    for (const t of toks) {
      if (seen.has(t)) {
        dupes.push(t);
        continue;
      }
      seen.add(t);
      added.push(t);
    }
    if (added.length > 0) onChange([...value, ...added]);
    setWarn(
      dupes.length > 0
        ? `Bỏ qua ${dupes.length} serial trùng. Đã thêm ${added.length}.`
        : added.length > 0
        ? `Đã thêm ${added.length} serial.`
        : "Không có serial mới.",
    );
    setPasteValue("");
  }

  function remove(s: string) {
    onChange(value.filter((x) => x !== s));
  }

  function clearAll() {
    if (!value.length) return;
    if (window.confirm(`Xoá tất cả ${value.length} serials?`)) onChange([]);
  }

  const overCount = expectedCount > 0 && value.length > expectedCount;

  return (
    <div className="space-y-2">
      <div className="flex items-end justify-between gap-2">
        <Label>Serials</Label>
        <div className="flex items-center gap-1 text-xs">
          <button
            type="button"
            onClick={() => setMode("scan")}
            className={cn(
              "rounded px-2 py-1 inline-flex items-center gap-1",
              mode === "scan" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted",
            )}
          >
            <ScanLine size={12} /> Quét
          </button>
          <button
            type="button"
            onClick={() => setMode("paste")}
            className={cn(
              "rounded px-2 py-1 inline-flex items-center gap-1",
              mode === "paste" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted",
            )}
          >
            <ClipboardPaste size={12} /> Dán danh sách
          </button>
        </div>
      </div>

      {mode === "scan" ? (
        <Input
          ref={scanRef}
          value={scanValue}
          onChange={(e) => setScanValue(e.target.value)}
          onKeyDown={handleScanKey}
          placeholder="Quét máy (tự thêm) hoặc gõ tay rồi Enter"
          className="font-mono"
          autoComplete="off"
        />
      ) : (
        <div className="space-y-2">
          <textarea
            className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm font-mono resize-y"
            placeholder="Mỗi serial 1 dòng, hoặc cách nhau bằng dấu phẩy / space"
            value={pasteValue}
            onChange={(e) => setPasteValue(e.target.value)}
          />
          <Button size="sm" variant="outline" onClick={commitPaste} disabled={!pasteValue.trim()}>
            Thêm vào danh sách
          </Button>
        </div>
      )}

      {warn && <p className="text-xs text-amber-600 dark:text-amber-400">{warn}</p>}

      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span className={overCount ? "text-amber-600 dark:text-amber-400" : ""}>
          Đã có <b className="tabular-nums">{value.length}</b>
          {expectedCount > 0 && (
            <> / <span className="tabular-nums">{expectedCount}</span></>
          )}
        </span>
        {value.length > 0 && (
          <button type="button" onClick={clearAll} className="text-destructive hover:underline">
            Xoá tất cả
          </button>
        )}
      </div>

      {value.length > 0 && (
        <ul className="flex flex-wrap gap-1.5 rounded-md border bg-muted/30 p-2 max-h-36 overflow-y-auto">
          {value.map((s) => (
            <li
              key={s}
              className="inline-flex items-center gap-1 rounded bg-background border px-2 py-0.5 text-xs font-mono"
            >
              <span>{s}</span>
              <button
                type="button"
                onClick={() => remove(s)}
                className="text-muted-foreground hover:text-destructive"
                aria-label={`Xoá ${s}`}
              >
                <X size={11} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
